import {
  Box,
  Button,
  Chip,
  Container,
  Stack,
  styled,
  type Theme,
  Typography,
} from '@mui/material';
import cx from 'clsx';
import { useManagePath } from '../../context';
import { useNotification } from '@/context/notification';
import { ResourceTypes } from '@/data/learning-path/interfaces';
import {
  useDiscardDraft,
  useEdit,
  usePublish,
} from '@/data/learning-path/path-management';
import { useScreenSizes } from '@/hooks/use-screen-sizes';

const BarContainer = styled(Container)({
  '&.isMediumScreen': {
    maxWidth: '120vw',
  },
});

const handleChipStatus = (
  isDraftVersion: boolean,
  alreadyPublished: boolean,
  hasContentChanged: boolean
) => {
  const createStatusChip = (
    backgroundColor: string | ((theme: Theme) => string),
    textColor: string | ((theme: Theme) => string),
    title: string,
    message: string
  ) => (
    <Box sx={{ maxWidth: { xs: '100%', md: '65%' } }}>
      <Chip
        label={
          <Typography
            variant="body2"
            sx={(theme) => ({
              color:
                typeof textColor === 'function' ? textColor(theme) : textColor,
            })}
          >
            <strong>{title}</strong> {message}
          </Typography>
        }
        size="small"
        sx={(theme) => ({
          backgroundColor:
            typeof backgroundColor === 'function'
              ? backgroundColor(theme)
              : backgroundColor,
          height: 'auto',
          '& .MuiChip-label': {
            whiteSpace: 'normal',
            padding: '8px 12px',
          },
        })}
      />
    </Box>
  );

  if (!isDraftVersion && !hasContentChanged) {
    return createStatusChip(
      (theme) => theme.palette.success.light,
      (theme) => theme.palette.success.dark,
      'Publicada:',
      'Esta trilha está publicada e todas as alterações estão salvas.'
    );
  } else {
    return createStatusChip(
      '#FFE6D380',
      '#A03500',
      'Rascunho não publicado:',
      alreadyPublished
        ? 'Esta é a versão Rascunho da trilha. A versão Publicada continua disponível para os usuários. Publique para deixar esta versão disponível, salve para continuar depois ou descarte o rascunho após salvá-lo para voltar à versão publicada.'
        : 'Esta é a versão Rascunho da trilha. Ela ainda não está disponível para os usuários. Publique para deixá-la disponível ou salve para continuar depois.'
    );
  }
};

export const ActionButtons = () => {
  const { isMediumScreen } = useScreenSizes();
  const { toast } = useNotification();
  const {
    headerImage,
    contents,
    pathId,
    title,
    description,
    hasContentChanged,
    setHasContentChanged,
    hasImageChanged,
    isDraftVersion,
    setIsDraftVersion,
    alreadyPublished,
    setAlreadyPublished,
  } = useManagePath();
  const { mutate: edit, isPending: editPending } = useEdit();
  const { mutate: publish, isPending: publishPending } = usePublish();
  const { mutate: discardDraft, isPending: discardDraftPending } =
    useDiscardDraft();

  const isPending = editPending || publishPending || discardDraftPending;

  const contentsSorted = contents
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((block, index) => ({
      ...block,
      position: index,
    }));

  const hasInvalidBlocks = () => {
    const invalidText = contents.some(
      (block) =>
        block.type === ResourceTypes.Text && !block.content?.body?.trim()
    );

    const invalidResources = contents.some(
      (block) =>
        block.type === ResourceTypes.ResourceGroup &&
        block.content?.resource_groups?.length === 0
    );

    if (invalidText || invalidResources) {
      toast({
        type: 'error',
        content: `Preencha todos os blocos: ${
          invalidText ? 'texto vazio' : ''
        }${invalidText && invalidResources ? ' e ' : ''}${
          invalidResources ? 'recurso ausente' : ''
        }.`,
      });
      return true;
    }

    return false;
  };

  const handleOnDraft = () => {
    if (hasInvalidBlocks()) return;

    edit(
      {
        id: pathId,
        title,
        description,
        header_image: headerImage,
        has_image_changed: hasImageChanged,
        learning_path: {
          contents: contentsSorted,
        },
      },
      {
        onSuccess: () => {
          setIsDraftVersion(true);
          setHasContentChanged(false);
          toast({
            content: 'As alterações foram salvas com sucesso!',
            type: 'success',
          });
        },
        onError: () => {
          toast({
            content:
              'Ocorreu um erro ao tentar salvar a trilha. Tente novamente mais tarde',
            type: 'error',
          });
        },
      }
    );
  };

  const handleOnSave = () => {
    if (hasInvalidBlocks()) return;

    edit(
      {
        id: pathId,
        title,
        description,
        header_image: headerImage,
        has_image_changed: hasImageChanged,
        learning_path: {
          contents: contentsSorted,
        },
      },
      {
        onSuccess: () => {
          publish(
            { pathId },
            {
              onSuccess: () => {
                setIsDraftVersion(false);
                setAlreadyPublished(true);
                setHasContentChanged(false);
                toast({
                  content: 'Alterações salvas e trilha publicada com sucesso!',
                  type: 'success',
                });
              },
              onError: () => {
                toast({
                  content:
                    'Ocorreu um erro ao tentar publicar a trilha. Tente novamente mais tarde',
                  type: 'error',
                });
              },
            }
          );
        },
        onError: () => {
          toast({
            content:
              'Ocorreu um erro ao tentar publicar a trilha. Tente novamente mais tarde',
            type: 'error',
          });
        },
      }
    );
  };

  const handleOnDiscardDraft = () => {
    discardDraft(
      { pathId },
      {
        onSuccess: () => {
          toast({
            content: 'Rascunho descartado com sucesso!',
            type: 'success',
          });
        },
        onError: () => {
          toast({
            content:
              'Ocorreu um erro ao tentar descartar o rascunho. Tente novamente mais tarde',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <Stack bgcolor="neutral.0" py={2} my={3}>
      <BarContainer fixed maxWidth="lg" className={cx({ isMediumScreen })}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          {handleChipStatus(
            isDraftVersion,
            alreadyPublished,
            hasContentChanged
          )}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{
              flexShrink: 0,
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            {isDraftVersion && alreadyPublished && (
              <Button
                data-testid="discard-draft-button"
                variant="text"
                onClick={handleOnDiscardDraft}
                disabled={isPending}
              >
                Descartar rascunho
              </Button>
            )}
            <Button
              data-testid="save-draft-button"
              variant="text"
              onClick={handleOnDraft}
              disabled={isPending}
            >
              Salvar sem publicar
            </Button>
            <Button
              data-testid="publish-button"
              variant="contained"
              onClick={handleOnSave}
              disabled={isPending}
              loading={isPending}
            >
              Publicar
            </Button>
          </Stack>
        </Stack>
      </BarContainer>
    </Stack>
  );
};
